import React, {useCallback, useEffect} from 'react';
import {useNavigate} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {RootState} from '../../../../../redux/store';
import {FileCatalystPopUpModal} from './FileCatalystPopUpModal';
import {setShowModal} from '../../../../../redux/slices/unSaveChangesModalSlice';
import {LeaveActionType} from '../UploadFileCatalyst';
import {isEmpty} from 'lodash';

interface LeaveFileCatalystConfirmationDialogProps {
  files: number;
  isShowLeaveModalFrom?: LeaveActionType;
  onLeavePageConfirm: (isLeave: boolean) => void;
}

const LeaveFileCatalystConfirmationDialog: React.FC<
  LeaveFileCatalystConfirmationDialogProps
> = ({files, isShowLeaveModalFrom, onLeavePageConfirm}) => {
  const showModal = useSelector(
    (state: RootState) => state.unsavedChangesModal.showModal
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const closeDialog = useCallback(
    () => dispatch(setShowModal(false)),
    [dispatch]
  );

  const onNavigatePage = useCallback(() => {
    const indexLink = localStorage?.getItem('currentLink');

    if (indexLink) {
      navigate(indexLink, {state: {source: 'nav-panel'}});
      localStorage?.removeItem('currentLink');
    } else {
      const link: string = localStorage?.getItem('currentPath') || '';
      if (isEmpty(link) || link === '/upload-file-catalyst') {
        navigate('/upload-evidence/new-evidence');
      } else {
        navigate(link, {state: {source: 'nav-panel'}});
      }
    }
    localStorage?.setItem('currentPath', '');
  }, [navigate]);

  useEffect(() => {
    if (showModal && files <= 0) {
      closeDialog();
      onNavigatePage();
    }
  }, [closeDialog, files, onNavigatePage, showModal]);

  useEffect(() => {
    closeDialog();
    files <= 0
      ? localStorage.setItem('currentPath', '')
      : localStorage.setItem('currentPath', globalThis.location.pathname);

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (files <= 0) return;

      event.preventDefault();
      event.returnValue = '';
    };

    const handlePopState = () => {
      dispatch(setShowModal(true));
      globalThis.history.pushState(null, '', globalThis.location.pathname);
    };

    globalThis.history.pushState(null, '', globalThis.location.pathname);

    globalThis.addEventListener('beforeunload', handleBeforeUnload);
    globalThis.addEventListener('popstate', handlePopState);

    return () => {
      globalThis.removeEventListener('beforeunload', handleBeforeUnload);
      globalThis.removeEventListener('popstate', handlePopState);
    };
  }, [closeDialog, dispatch, files]);

  const onLeavePage = useCallback(() => {
    closeDialog();
    if (isShowLeaveModalFrom) {
      onLeavePageConfirm(true);
      localStorage?.setItem('currentPath', '');
    } else {
      onNavigatePage();
    }
  }, [closeDialog, isShowLeaveModalFrom, onLeavePageConfirm, onNavigatePage]);

  const onStayPage = useCallback(() => {
    localStorage?.removeItem('currentLink');
    closeDialog();
    if (isShowLeaveModalFrom) {
      onLeavePageConfirm(false);
    }
  }, [closeDialog, isShowLeaveModalFrom, onLeavePageConfirm]);

  return files > 0 && showModal ? (
    <FileCatalystPopUpModal
      type={'leave'}
      onYes={onLeavePage}
      onClose={onStayPage}
      isShow={showModal}
    />
  ) : null;
};

export default LeaveFileCatalystConfirmationDialog;
